import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight } from "lucide-react";
import { FOLIE, calcPrice, fmtPln, usePricingAdjustments, MAX_HEIGHT_MM, MAX_WIDTH_MM } from "@/lib/pricing";

const QTY_OPTIONS = [50, 100, 200, 300, 500];

interface QuickQuoteProps {
  heading?: string;
  defaultWidth?: string;
  defaultHeight?: string;
  defaultQty?: number;
  defaultFoil?: string;
}

/** Compact quote widget embedded on the homepage and product pages – full quote lives on /kalkulator. */
const QuickQuote = ({
  heading = "Szybka wycena naklejek 3D",
  defaultWidth = "50",
  defaultHeight = "50",
  defaultQty = 100,
  defaultFoil = FOLIE[0].key,
}: QuickQuoteProps) => {
  const navigate = useNavigate();
  const { adjustPercent, foilAdjustments, getQtyTier } = usePricingAdjustments();
  const [w, setW] = useState(defaultWidth);
  const [h, setH] = useState(defaultHeight);
  const [qty, setQty] = useState(defaultQty);
  const [foil, setFoil] = useState(defaultFoil);

  const result = useMemo(() => {
    const wv = parseFloat(w);
    const hv = parseFloat(h);
    if (!wv || !hv || wv > MAX_WIDTH_MM || hv > MAX_HEIGHT_MM) return null;
    const { adj: qtyAdj, baseQty } = getQtyTier(foil, qty);
    return calcPrice(wv, hv, qty, "rect", adjustPercent, foilAdjustments[foil] || 0, qtyAdj, baseQty);
  }, [w, h, qty, foil, adjustPercent, foilAdjustments, getQtyTier]);

  const goFull = () => {
    navigate(`/kalkulator?w=${encodeURIComponent(w)}&h=${encodeURIComponent(h)}&qty=${qty}&folia=${foil}&shape=rect`);
  };

  return (
    <div className="bg-card/95 backdrop-blur-sm border border-border rounded-2xl p-5 md:p-6 shadow-xl text-left">
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="w-5 h-5 text-accent" aria-hidden="true" />
        <h2 className="text-base md:text-lg font-bold text-foreground">{heading}</h2>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className="block text-xs font-medium text-muted-foreground mb-1">Szerokość (mm)</span>
          <input
            type="number"
            inputMode="numeric"
            value={w}
            onChange={(e) => setW(e.target.value)}
            min={5}
            max={MAX_WIDTH_MM}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-muted-foreground mb-1">Wysokość (mm)</span>
          <input
            type="number"
            inputMode="numeric"
            value={h}
            onChange={(e) => setH(e.target.value)}
            min={5}
            max={MAX_HEIGHT_MM}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
      </div>

      <label className="block mb-3">
        <span className="block text-xs font-medium text-muted-foreground mb-1">Rodzaj folii</span>
        <select
          value={foil}
          onChange={(e) => setFoil(e.target.value)}
          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
        >
          {FOLIE.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
      </label>

      <fieldset className="mb-4">
        <legend className="block text-xs font-medium text-muted-foreground mb-1">Nakład (szt.)</legend>
        <div className="flex flex-wrap gap-2">
          {QTY_OPTIONS.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setQty(q)}
              aria-pressed={qty === q}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                qty === q ? "bg-accent text-accent-foreground border-accent" : "border-border text-foreground hover:border-accent"
              }`}
            >
              {q}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="rounded-xl bg-secondary p-4 mb-4">
        {result ? (
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Cena orientacyjna netto</p>
              <p className="text-2xl md:text-3xl font-bold text-foreground">{fmtPln(result.totalNet)} zł</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtPln(result.unitNet)} zł / szt. netto</p>
            </div>
            <p className="text-xs text-muted-foreground text-right">
              {fmtPln(result.totalGross)} zł brutto<br />
              Minimum logistyczne: 100 zł netto
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Podaj wymiary do {MAX_WIDTH_MM} × {MAX_HEIGHT_MM} mm, aby zobaczyć cenę.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={goFull}
        className="w-full gradient-gold text-accent-foreground font-bold px-5 py-3 rounded-lg inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-gold"
      >
        Dokładna wycena i zamówienie
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
      <p className="text-[11px] text-muted-foreground mt-2">
        Wycena wstępna dla kształtu prostokątnego. Kształt po obrysie i większe nakłady wyceniamy indywidualnie.
      </p>
    </div>
  );
};

export default QuickQuote;
